import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { computeMultiLineRevenue } from '../utils/revenueUtils';
import { useCurrency } from '../../../../hooks/useCurrency';

/**
 * Gauge normalization — the yield bar is a *contextual* gauge, not a literal
 * 0–100% representation. Expected advisory business yields fall within a
 * narrow band; we map that band onto the full gauge so the visual stays
 * meaningful for real-world numbers (e.g. 5.2% should read as "maxed out").
 */
export const YIELD_GAUGE_MIN = 0.5; // % — below this, gauge is empty
export const YIELD_GAUGE_MAX = 1.5; // % — above this, gauge is full

/**
 * Compute the user's yield-on-assets KPI.
 *
 * Returns an object with:
 *   - aum                   raw number (null when unavailable)
 *   - recurringIncome       raw number (null when unavailable)
 *   - yieldPercent          number (e.g. 5.2) or null if unavailable / AUM is 0
 *   - gaugeProgress         0–100 clamped against the [MIN, MAX] band
 *
 * Reuses `computeMultiLineRevenue` so the recurring-income number matches
 * the Revenue Mix donut / profile stats exactly — no duplicated business logic.
 */
export const computeIncomeYieldOnAssets = (user, company) => {
  const rawAum = user && user.assets_under_management;
  const aumNum = rawAum == null || rawAum === '' ? null : Number(rawAum);
  const aum = Number.isFinite(aumNum) && aumNum > 0 ? aumNum : null;

  const { totals } = computeMultiLineRevenue(user || {}, company || {});
  const recurring = Number.isFinite(totals.recurring) && totals.recurring > 0
    ? totals.recurring
    : null;

  const yieldPercent = aum && recurring != null
    ? (recurring / aum) * 100
    : null;

  let gaugeProgress = 0;
  if (yieldPercent != null) {
    const span = YIELD_GAUGE_MAX - YIELD_GAUGE_MIN;
    const raw = ((yieldPercent - YIELD_GAUGE_MIN) / span) * 100;
    gaugeProgress = Math.max(0, Math.min(100, raw));
  }

  return { aum, recurringIncome: recurring, yieldPercent, gaugeProgress };
};

/**
 * IncomeYieldOnAssetsCard
 *
 * Feature-gated replacement for the Compensation Mix donut on the Profile
 * Drawer. Renders a compact KPI card showing AUM/FUM, Recurring Income, the
 * derived yield, and a contextual gauge normalized against the expected
 * advisory-business yield band.
 *
 * Empty-value semantics:
 *   - AUM/FUM or Recurring Income missing → show "—"
 *   - Yield uncomputable (missing data or AUM = 0) → yield shows "—" and
 *     the gauge renders in a subdued disabled state (no fill, no ARIA value).
 */
const IncomeYieldOnAssetsCard = ({ user, company }) => {
  const intl = useIntl();
  const { symbol: currencySym } = useCurrency();

  const { aum, recurringIncome, yieldPercent, gaugeProgress } =
    computeIncomeYieldOnAssets(user, company);

  const placeholder = '—';

  // Short-form currency — same recipe used by ProfileStats so numbers read
  // at the same scale across the drawer.
  const formatCurrencyShort = (value) => {
    if (value == null || !Number.isFinite(value)) return placeholder;
    if (value >= 1_000_000) return `${currencySym}${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `${currencySym}${(value / 1_000).toFixed(0)}K`;
    return `${currencySym}${Number(value).toLocaleString()}`;
  };

  const yieldDisplay = yieldPercent == null
    ? placeholder
    : intl.formatNumber(yieldPercent / 100, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const gaugeDisabled = yieldPercent == null;

  return (
    <div className="space-y-4">
      <h4 className="text-base font-medium text-succession-text-primary text-center">
        {intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.income_yield_on_assets' })}
      </h4>

      <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-4 space-y-4">
        {/* Meta rows: FUM / AUM + Recurring Income */}
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <dt className="text-succession-text-muted">
              {intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.assets_under_management' })}
            </dt>
            <dd className="text-succession-text-primary font-medium">
              {formatCurrencyShort(aum)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-2">
            <dt className="text-succession-text-muted">
              {intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.recurring_income' })}
            </dt>
            <dd className="text-succession-text-primary font-medium">
              {formatCurrencyShort(recurringIncome)}
            </dd>
          </div>
        </dl>

        <div className="border-t border-succession-border" aria-hidden="true" />

        {/* Headline KPI: yield */}
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-succession-text-muted">
            {intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.yield' })}
          </span>
          <span className="text-2xl font-bold text-succession-text-primary">
            {yieldDisplay}
          </span>
        </div>

        {/* Normalized gauge — contextual, not a literal 0–100% */}
        <div
          role="progressbar"
          aria-label={intl.formatMessage({ id: 'succession.profile_drawer.profile_charts.yield_gauge_aria' })}
          aria-valuemin={gaugeDisabled ? undefined : 0}
          aria-valuemax={gaugeDisabled ? undefined : 100}
          aria-valuenow={gaugeDisabled ? undefined : Math.round(gaugeProgress)}
          aria-disabled={gaugeDisabled || undefined}
          className="relative h-2 w-full rounded-full overflow-hidden bg-succession-bg-secondary"
        >
          {!gaugeDisabled && (
            <div
              className="h-full rounded-full transition-[width] duration-500 ease-out"
              style={{
                width: `${gaugeProgress}%`,
                backgroundColor: 'var(--succession-primary, #3b82f6)',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

IncomeYieldOnAssetsCard.propTypes = {
  user: PropTypes.shape({
    assets_under_management: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    recurring_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fee_based_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    trailer_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    direct_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  company: PropTypes.shape({
    feature_types: PropTypes.arrayOf(PropTypes.string),
    features: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.object]),
  }),
};

export default IncomeYieldOnAssetsCard;
